import { useEffect } from 'react'

import { NotificationPortal } from '@krp/ui-kit'
import type { ShellNotice } from '@krp/client-runtime'

import { SHELL_NOTIFICATIONS, type ModuleNotifications as ModuleNotificationsEntry } from '../composition/shell'
import { useAppDispatch, useAppSelector } from './hooks'
import type { RootState } from './types'

/** Итоги команд окон всех модулей живут выше маршрута и появляются в одной области. Что и как показать, объявляет модуль (`UiModule.shell`). */
export function Notifications() {
  return SHELL_NOTIFICATIONS.map(({ moduleId, notifications }) => (
    <ModuleNotifications key={moduleId} moduleId={moduleId} notifications={notifications} />
  ))
}

/** Срез модуля по его ключу в корне состояния; ключа нет — `undefined`. Ссылка на срез та же, что в store, поэтому перерисовок не добавляет. */
const selectModuleState = (state: RootState, moduleId: string): unknown =>
  Object.entries(state).find(([key]) => key === moduleId)?.[1]

function ModuleNotifications({ moduleId, notifications }: ModuleNotificationsEntry) {
  const dispatch = useAppDispatch()
  // Срез модуля стабилен между рендерами; уведомления из него строятся уже в рендере.
  const moduleState = useAppSelector((state) => selectModuleState(state, moduleId))
  return Object.entries(notifications.select(moduleState)).map(([key, notice]) => (
    <Notification key={key} notice={notice} onClose={() => dispatch(notifications.clear(key))} />
  ))
}

function Notification({ notice, onClose }: { notice: ShellNotice; onClose: () => void }) {
  const { autoCloseMs } = notice
  useEffect(() => {
    if (autoCloseMs === null) return
    const timeout = window.setTimeout(onClose, autoCloseMs)
    return () => window.clearTimeout(timeout)
  }, [autoCloseMs, notice, onClose])
  return <NotificationPortal tone={notice.tone} title={notice.title} text={notice.text} hint={notice.hint} meta={notice.meta} onClose={onClose} />
}
